import { useCallback, useEffect, useState } from "react";
import {
  listTickets,
  submitSupportTicket,
  submitTicketFromLiveChat,
  submitTicketAgentResponse,
  updateTicketGroup,
} from "@/pages/tickets/api/ticketsApi";
import { patchTicketSlaForAgentReply } from "@/lib/slaMetaPatch";
import { normalizeTicketStatus } from "@/pages/tickets/api/ticketStorage";

/**
 * @typedef {{ id: string, conversationId: string, subject: string, requesterEmail: string, openedBy: 'agent' | 'user' | 'bot', createdAt: string, status: string, conversationSummary: string, statusChangedAt?: string, group?: 'refund' | 'technical' }} SupportTicket
 */

/**
 * Support ticket queue — all persistence via ticketsApi (skeleton).
 */
export function useTickets(workspaceId = null) {
  const [tickets, setTickets] = useState([]);
  const [ticketQueueSections, setTicketQueueSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        const { data } = await listTickets({ workspaceId });
        setTickets(Array.isArray(data?.tickets) ? data.tickets : []);
        setTicketQueueSections(Array.isArray(data?.sections) ? data.sections : []);
      } catch {
        if (!silent) {
          setTickets([]);
          setTicketQueueSections([]);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    void refresh({ silent: false });
  }, [refresh]);

  const createTicket = useCallback(
    /**
     * @param {{ conversationId: string, subject: string, requesterEmail: string, conversationSummary: string, openedBy?: 'agent' | 'user' | 'bot' }} input
     * @returns {Promise<SupportTicket>}
     */
    async (input) => {
      const { data: ticket } = await submitSupportTicket(input, { workspaceId });
      await refresh({ silent: true });
      return /** @type {SupportTicket} */ (ticket);
    },
    [refresh, workspaceId],
  );

  const createTicketFromLiveChat = useCallback(
    async (input) => {
      const { data: ticket, warnings } = await submitTicketFromLiveChat(input, { workspaceId });
      await refresh({ silent: true });
      return { ticket, warnings };
    },
    [refresh, workspaceId],
  );

  const submitTicketWithStatus = useCallback(
    async (input) => {
      const { data: ticket } = await submitTicketAgentResponse(input, { workspaceId });
      const next = { ...ticket, status: normalizeTicketStatus(input.newStatus) };
      const sentAt = new Date().toISOString();
      const withSla = patchTicketSlaForAgentReply(next, sentAt, {
        isNote: input.senderTab === "note",
      });
      setTickets((prev) => prev.map((t) => (t.id === withSla.id ? { ...t, ...withSla } : t)));
      return withSla;
    },
    [workspaceId],
  );

  const setTicketGroup = useCallback(
    async (input) => {
      const { data: ticket } = await updateTicketGroup(input, { workspaceId });
      // Apply PATCH response immediately — list refresh can race the DB commit and
      // return a cached queue that still has the old group.
      setTickets((prev) => prev.map((t) => (t.id === ticket.id ? ticket : t)));
      return ticket;
    },
    [workspaceId],
  );

  return {
    tickets,
    ticketQueueSections,
    loading,
    refresh,
    refreshTickets: refresh,
    createTicket,
    createTicketFromLiveChat,
    submitTicketWithStatus,
    setTicketGroup,
  };
}
